import { useCallback, useEffect, useRef, useState } from "react";
import type { WhatsAppTemplateDto } from "@multi-chat/shared";
import type {
  CreateWhatsAppTemplateRequest,
  EditWhatsAppTemplateRequest,
} from "@multi-chat/shared";
import { isApiError, isBackendUnavailable } from "../../api/client.js";
import {
  attachWhatsAppTemplateHeaderImage,
  createWhatsAppTemplate,
  deleteWhatsAppTemplate,
  listWhatsAppTemplates,
  syncWhatsAppTemplates,
  updateWhatsAppTemplate,
} from "../../api/whatsapp-api.js";
import { whatsAppTemplatesDisabledClientState } from "./whatsapp-templates-access.js";

export type TemplatesLoadState = "loading" | "ready" | "error";

export interface UseWhatsAppTemplatesResult {
  loadState: TemplatesLoadState;
  items: readonly WhatsAppTemplateDto[];
  lastSyncedAt: string | null;
  loadError: string | null;
  syncing: boolean;
  syncError: string | null;
  clearSyncError: () => void;
  refreshFromProvider: () => Promise<void>;
  reload: () => void;
  upsertTemplateItem: (item: WhatsAppTemplateDto) => void;
  removeTemplateItem: (templateId: number) => void;
  createTemplate: (
    payload: CreateWhatsAppTemplateRequest,
    options?: { headerImage?: File | null; signal?: AbortSignal },
  ) => Promise<WhatsAppTemplateDto>;
  updateTemplate: (
    templateId: number,
    payload: EditWhatsAppTemplateRequest,
    signal?: AbortSignal,
  ) => Promise<WhatsAppTemplateDto>;
  deleteTemplate: (templateId: number, signal?: AbortSignal) => Promise<WhatsAppTemplateDto>;
  attachHeaderImage: (templateId: number, file: File, signal?: AbortSignal) => Promise<WhatsAppTemplateDto>;
}

export function useWhatsAppTemplates(
  organizationId: number,
  enabled: boolean,
  unreachableMessage: string,
): UseWhatsAppTemplatesResult {
  const [loadState, setLoadState] = useState<TemplatesLoadState>("loading");
  const [items, setItems] = useState<readonly WhatsAppTemplateDto[]>([]);
  const [lastSyncedAt, setLastSyncedAt] = useState<string | null>(null);
  const [loadError, setLoadError] = useState<string | null>(null);
  const [syncing, setSyncing] = useState(false);
  const [syncError, setSyncError] = useState<string | null>(null);
  const [reloadToken, setReloadToken] = useState(0);

  const orgIdRef = useRef(organizationId);
  orgIdRef.current = organizationId;
  const unreachableRef = useRef(unreachableMessage);
  unreachableRef.current = unreachableMessage;
  const syncAbortRef = useRef<AbortController | null>(null);

  const clearSyncError = useCallback(() => setSyncError(null), []);

  const reload = useCallback(() => setReloadToken((token) => token + 1), []);

  const upsertTemplateItem = useCallback((item: WhatsAppTemplateDto) => {
    setItems((previous) => {
      const index = previous.findIndex((row) => row.id === item.id);
      if (index === -1) {
        return [...previous, item].toSorted((a, b) =>
          a.name.localeCompare(b.name) || a.language.localeCompare(b.language),
        );
      }
      const next = [...previous];
      next[index] = item;
      return next;
    });
  }, []);

  const removeTemplateItem = useCallback((templateId: number) => {
    setItems((previous) => previous.filter((row) => row.id !== templateId));
  }, []);

  const toMessage = useCallback((error: unknown): string => {
    if (isBackendUnavailable(error)) return unreachableRef.current;
    if (isApiError(error)) return error.message;
    return unreachableRef.current;
  }, []);

  useEffect(() => {
    if (!enabled) {
      setLoadState(whatsAppTemplatesDisabledClientState.loadState);
      setItems(whatsAppTemplatesDisabledClientState.items);
      setLastSyncedAt(whatsAppTemplatesDisabledClientState.lastSyncedAt);
      setLoadError(whatsAppTemplatesDisabledClientState.loadError);
      setSyncError(whatsAppTemplatesDisabledClientState.syncError);
      setSyncing(whatsAppTemplatesDisabledClientState.syncing);
      return;
    }

    const controller = new AbortController();
    const currentOrgId = organizationId;
    setLoadState("loading");
    setLoadError(null);
    setSyncError(null);

    (async () => {
      try {
        const response = await listWhatsAppTemplates(currentOrgId, controller.signal);
        if (orgIdRef.current !== currentOrgId) return;
        setItems(response.items);
        setLastSyncedAt(response.lastSyncedAt);
        setLoadState("ready");
      } catch (error) {
        if (controller.signal.aborted) return;
        if (orgIdRef.current !== currentOrgId) return;
        setLoadError(toMessage(error));
        setLoadState("error");
      }
    })();

    return () => {
      controller.abort();
      syncAbortRef.current?.abort();
      syncAbortRef.current = null;
    };
  }, [organizationId, enabled, reloadToken, toMessage]);

  const refreshFromProvider = useCallback(async () => {
    if (syncing) return;

    syncAbortRef.current?.abort();
    const controller = new AbortController();
    syncAbortRef.current = controller;
    const currentOrgId = orgIdRef.current;

    setSyncing(true);
    setSyncError(null);

    try {
      const response = await syncWhatsAppTemplates(currentOrgId, controller.signal);
      if (orgIdRef.current !== currentOrgId || controller.signal.aborted) return;
      setItems(response.items);
      setLastSyncedAt(response.syncedAt);
      setLoadState("ready");
      setLoadError(null);
    } catch (error) {
      if (controller.signal.aborted) return;
      if (orgIdRef.current !== currentOrgId) return;
      setSyncError(toMessage(error));
    } finally {
      if (syncAbortRef.current === controller) {
        syncAbortRef.current = null;
      }
      if (orgIdRef.current === currentOrgId) {
        setSyncing(false);
      }
    }
  }, [syncing, toMessage]);

  const createTemplateMutation = useCallback(
    async (
      payload: CreateWhatsAppTemplateRequest,
      options?: { headerImage?: File | null; signal?: AbortSignal },
    ) => {
      const response = await createWhatsAppTemplate(orgIdRef.current, payload, options);
      upsertTemplateItem(response.item);
      return response.item;
    },
    [upsertTemplateItem],
  );

  const updateTemplateMutation = useCallback(
    async (templateId: number, payload: EditWhatsAppTemplateRequest, signal?: AbortSignal) => {
      const response = await updateWhatsAppTemplate(
        orgIdRef.current,
        templateId,
        payload,
        signal,
      );
      upsertTemplateItem(response.item);
      return response.item;
    },
    [upsertTemplateItem],
  );

  const deleteTemplateMutation = useCallback(
    async (templateId: number, signal?: AbortSignal) => {
      const response = await deleteWhatsAppTemplate(orgIdRef.current, templateId, signal);
      return response.item;
    },
    [],
  );

  const attachHeaderImageMutation = useCallback(
    async (templateId: number, file: File, signal?: AbortSignal) => {
      const response = await attachWhatsAppTemplateHeaderImage(
        orgIdRef.current,
        templateId,
        file,
        signal,
      );
      upsertTemplateItem(response.item);
      return response.item;
    },
    [upsertTemplateItem],
  );

  return {
    loadState,
    items,
    lastSyncedAt,
    loadError,
    syncing,
    syncError,
    clearSyncError,
    refreshFromProvider,
    reload,
    upsertTemplateItem,
    removeTemplateItem,
    createTemplate: createTemplateMutation,
    updateTemplate: updateTemplateMutation,
    deleteTemplate: deleteTemplateMutation,
    attachHeaderImage: attachHeaderImageMutation,
  };
}
